/**
 * Score breakdown.
 *
 * "No opaque total score" (FR-203): the headline is shown first, then each
 * component's contribution, the weight actually applied, where the number came
 * from, and what had no data at all. Progressive disclosure keeps the list
 * scannable while making the full trail one click away.
 */

import type { AssetScoreOut, ScoreComponent } from '../lib/types'
import { percent, titleCase } from '../lib/format'
import { DataClassChip } from './Chips'

function ComponentRow({ component }: { component: ScoreComponent }) {
  const share = component.weight > 0 ? (component.normalised ?? 0) : 0
  return (
    <tr>
      <td>
        <div style={{ fontWeight: 550 }}>{component.label}</div>
        {component.explanation ? (
          <div className="tiny faint">{component.explanation}</div>
        ) : null}
      </td>
      <td style={{ width: 110 }}>
        {component.available ? (
          <div className="bar" title={`Normalised ${percent(share, 1)}`}>
            <span style={{ width: `${Math.max(2, share * 100)}%` }} />
          </div>
        ) : (
          <span className="tiny faint">no data</span>
        )}
      </td>
      <td className="num tnum nowrap" style={{ width: 84 }}>
        {component.available ? percent(component.weight, 0) : '—'}
        {component.available && Math.abs(component.weight - component.configured_weight) > 0.001 ? (
          <span
            className="tiny faint"
            title={
              `Configured at ${percent(component.configured_weight)}. It was raised because ` +
              'other components had no data and their weight was redistributed here.'
            }
          >
            {' '}
            ↑
          </span>
        ) : null}
      </td>
      <td className="num tnum" style={{ width: 64, fontWeight: 600 }}>
        {component.available ? component.contribution.toFixed(1) : '—'}
      </td>
      <td style={{ width: 108 }}>
        <DataClassChip value={component.data_class} />
      </td>
    </tr>
  )
}

export function ScoreBreakdown({ score }: { score: AssetScoreOut }) {
  const available = score.components.filter((c) => c.available)
  const missing = score.components.filter((c) => !c.available)
  const base = available.reduce((sum, c) => sum + c.contribution, 0)
  const penaltyTotal = score.penalties.reduce((sum, p) => sum + p.points, 0)
  const redistributed = missing.reduce((sum, c) => sum + c.configured_weight, 0)

  return (
    <div className="stack">
      <div className="row between">
        <div>
          <div className="metric" style={{ border: 0, padding: 0, background: 'transparent' }}>
            <div className="label">Suitability</div>
            <div className="value" style={{ fontSize: 30 }}>
              {score.total_score.toFixed(1)}
              <span className="faint" style={{ fontSize: 15, fontWeight: 500 }}>
                {' '}
                / 100
              </span>
            </div>
          </div>
        </div>
        <div className="col" style={{ alignItems: 'flex-end', gap: 4 }}>
          <span className="chip neutral mono" title="The scoring configuration this score came from">
            {score.scoring_version}
          </span>
          <span className="tiny faint">
            {available.length} of {score.components.length} components had data
          </span>
        </div>
      </div>

      {redistributed > 0.001 ? (
        <div className="callout warn">
          <span className="icon" aria-hidden>
            !
          </span>
          <div className="body">
            <strong>{percent(redistributed)} of the model had no data for this asset.</strong>
            <br />
            That weight was redistributed across the components that do have data, and the
            confidence was reduced. It was not scored as zero.
          </div>
        </div>
      ) : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Component</th>
              <th>Normalised</th>
              <th className="num">Weight</th>
              <th className="num">Points</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {[...available]
              .sort((a, b) => b.contribution - a.contribution)
              .map((c) => (
                <ComponentRow key={c.key} component={c} />
              ))}
            {missing.map((c) => (
              <ComponentRow key={c.key} component={c} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} style={{ fontWeight: 600 }}>
                Component subtotal
              </td>
              <td className="num tnum" style={{ fontWeight: 600 }}>
                {base.toFixed(1)}
              </td>
              <td />
            </tr>
            {score.penalties.length > 0 ? (
              <tr>
                <td colSpan={3} style={{ color: 'var(--danger)' }}>
                  Penalties
                </td>
                <td className="num tnum" style={{ color: 'var(--danger)', fontWeight: 600 }}>
                  −{penaltyTotal.toFixed(1)}
                </td>
                <td />
              </tr>
            ) : null}
            <tr>
              <td colSpan={3} style={{ fontWeight: 700 }}>
                Total
              </td>
              <td className="num tnum" style={{ fontWeight: 700 }}>
                {score.total_score.toFixed(1)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {score.penalties.length > 0 ? (
        <details className="disclosure">
          <summary>Why points were deducted ({score.penalties.length})</summary>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {score.penalties.map((p) => (
              <li key={p.key}>
                <strong>
                  {p.label} −{p.points.toFixed(1)}
                </strong>
                <br />
                <span className="muted">{p.reason}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {score.evidence.length > 0 ? (
        <details className="disclosure" open>
          <summary>Evidence</summary>
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {score.evidence.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {score.missing_features.length > 0 ? (
        <details className="disclosure">
          <summary>Features with no data ({score.missing_features.length})</summary>
          <div className="pill-row">
            {score.missing_features.map((key) => (
              <span key={key} className="chip unavailable">
                {titleCase(key.split('.').slice(1).join(' ') || key)}
              </span>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  )
}
